import * as React from 'react'
import TrueNativeEditor from './TrueNativeEditor'
import { TemplateProvider } from './templateManagement/TemplateProvider'
import { EditorProvider } from './editorContent/EditorProvider'
import { Template } from './templateManagement/templates/types'
import { EditorContent } from './editorContent/types'
import { useTrueNativeEditor } from './useTrueNativeEditor'
import { TrueNativeArticle } from './articleManagement/types'

interface Props {
	campaignId: string
	articleId?: string
	onSave?: () => void
	onCancel?: () => void
	initialData?: TrueNativeArticle
}

const TrueNativeEditorContainer = ({ campaignId, articleId, onSave, onCancel, initialData }: Props) => {
	// Article and lifecycle management (no editor state)
	const editorState = useTrueNativeEditor({
		campaignId,
		articleId,
		initialData,
		onSave: (data: TrueNativeArticle) => {
			// TODO: Add API call logic here
			// eslint-disable-next-line no-console
			console.log('Saving article:', data)
			onSave?.()
		},
		onCancel: () => {
			// TODO: Add unsaved changes check logic here
			onCancel?.()
		},
	})

	// State for template-driven content updates
	const [templateContent, setTemplateContent] = React.useState<EditorContent | undefined>(undefined)

	// Cross-domain communication handlers for TemplateProvider
	const handleTemplateApply = (template: Template) => {
		// Template application through externalContent prop
		setTemplateContent({
			html: template.html,
			css: template.css,
			wysiwyg: '', // Keep existing wysiwyg content when applying template
		})
	}

	const handleContentRequest = () => {
		// TemplateProvider requests current content for save operations
		// Content is now managed by EditorProvider, so we need to get it from there
		// This will be handled by the EditorProvider's content state
		return editorState.initialContent
	}

	const handleTitleRequest = () => {
		// TemplateProvider requests current title for save operations
		return editorState.title
	}

	const handleDirtyStateChange = (isDirty: boolean) => {
		// TemplateProvider notifies about dirty state changes
		if (isDirty) {
			editorState.handleDirtyStateChange(true)
		}
	}

	// Cross-domain communication handlers for EditorProvider
	const handleEditorContentUpdate = (content: EditorContent) => {
		// EditorProvider notifies about content changes
		// Sync the content back to the main hook for saving
		editorState.handleContentUpdate(content)
	}

	const handleEditorDirtyStateChange = (isDirty: boolean) => {
		// EditorProvider notifies about dirty state changes
		// Sync dirty state with main application state
		editorState.handleDirtyStateChange(isDirty)
	}

	// Clean provider composition with proper cross-domain communication
	return (
		<TemplateProvider
			onTemplateApply={handleTemplateApply}
			onContentRequest={handleContentRequest}
			onTitleRequest={handleTitleRequest}
			onDirtyStateChange={handleDirtyStateChange}
		>
			<EditorProvider
				initialContent={editorState.initialContent}
				externalContent={templateContent}
				onContentUpdate={handleEditorContentUpdate}
				onDirtyStateChange={handleEditorDirtyStateChange}
			>
				<TrueNativeEditor
					// Article state from hook (no editor state)
					activeArticleTab={editorState.activeArticleTab}
					title={editorState.title}
					isDirty={editorState.isDirty}
					// Article handlers from hook (no editor handlers)
					onTitleChange={editorState.handleTitleChange}
					onArticleTabSelect={editorState.handleArticleTabSelect}
					onSave={editorState.handleSave}
					onCancel={editorState.handleCancel}
				/>
			</EditorProvider>
		</TemplateProvider>
	)
}

export default TrueNativeEditorContainer
